/**
 * API Configuration - Single source of truth for the admin backoffice.
 *
 * Goals:
 * - One base-url resolution strategy (env first, then hostname heuristics)
 * - Canonical, real API endpoints only (no "fallback" paths)
 * - Centralized authenticated JSON fetch helper
 */

export class ApiError extends Error {
    status: number;
    detail: string;

    constructor(status: number, detail: string) {
        super(`HTTP ${status}: ${detail}`);
        this.status = status;
        this.detail = detail;
    }
}

// Determina l'URL base dell'API in base all'ambiente
export const getApiBaseUrl = (): string => {
    const envBase =
        import.meta.env.VITE_BACKEND_API_URL ||
        import.meta.env.VITE_API_BASE_URL ||
        "";
    if (typeof envBase === "string" && envBase.trim()) {
        return envBase.trim().replace(/\/+$/, "");
    }

    if (typeof window !== "undefined") {
        const hostname = window.location.hostname;

        // Sviluppo locale (Vite/dev server) senza env.
        if (hostname === "localhost" || hostname === "127.0.0.1") {
            return "http://localhost:8003";
        }

        // Production heuristic (no hard-coded domain):
        // If the site runs on foo.example.tld (or www.example.tld), assume the API is on api.example.tld.
        // This avoids a white-screen when VITE_* variables were not injected at build time.
        if (hostname && hostname !== "localhost") {
            const parts = hostname.split(".").filter(Boolean);
            if (parts.length >= 2) {
                const nextHost =
                    parts.length >= 3 ? ["api", ...parts.slice(1)].join(".") : `api.${hostname}`;
                const protocol = window.location.protocol || "https:";
                return `${protocol}//${nextHost}`;
            }
        }
    }

    const msg =
        "Missing API base URL. Set VITE_BACKEND_API_URL (recommended) or VITE_API_BASE_URL.";

    // Fail fast: without an API base URL the app cannot function correctly in production.
    throw new Error(msg);
};

// Helper per costruire URL API completi
export const apiUrl = (endpoint: string): string => {
    const baseUrl = getApiBaseUrl();
    const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    if (!baseUrl) return cleanEndpoint;
    return `${baseUrl}${cleanEndpoint}`;
};

type QueryParams = Record<
    string,
    string | number | boolean | null | undefined
>;

type ApiFetchOptions = RequestInit & {
    query?: QueryParams;
    skipAuthRefresh?: boolean;
    retry?: number;
    retryDelayMs?: number;
    timeoutMs?: number;
};

const _getStoredToken = (): string | null => {
    if (typeof window === "undefined") return null;
    const t = localStorage.getItem("auth_token");
    return t && t.trim() ? t.trim() : null;
};

const _getStoredRefreshToken = (): string | null => {
    if (typeof window === "undefined") return null;
    const t = localStorage.getItem("refresh_token");
    return t && t.trim() ? t.trim() : null;
};

const _decodeJwtPayload = (jwt: string): Record<string, any> | null => {
    try {
        const [, payloadPart] = jwt.split(".");
        if (!payloadPart) return null;
        const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
        const padded = normalized.padEnd(
            normalized.length + ((4 - (normalized.length % 4)) % 4),
            "=",
        );
        return JSON.parse(atob(padded));
    } catch {
        return null;
    }
};

const _isTokenExpired = (token: string, skewSeconds = 20): boolean => {
    const payload = _decodeJwtPayload(token);
    const exp = Number(payload?.exp || 0);
    if (!exp || !Number.isFinite(exp)) return false;
    const nowSeconds = Date.now() / 1000;
    return nowSeconds >= exp - skewSeconds;
};

export const clearStoredAuth = (): void => {
    if (typeof window === "undefined") return;
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("auth_user");
};

const _handleAuthExpired = (): void => {
    if (typeof window === "undefined") return;
    clearStoredAuth();
    window.dispatchEvent(new Event("ummr-auth-expired"));
    if (window.location.pathname.startsWith("/admin")) {
        window.location.replace("/login");
    }
};

let _refreshInFlight: Promise<string | null> | null = null;

export async function refreshAuthToken(): Promise<string | null> {
    if (_refreshInFlight) return _refreshInFlight;

    const refreshToken = _getStoredRefreshToken();
    if (!refreshToken) {
        _handleAuthExpired();
        return null;
    }

    _refreshInFlight = (async () => {
        try {
            const res = await fetch(apiUrl("/api/v1/auth/refresh"), {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });

            if (!res.ok) {
                _handleAuthExpired();
                return null;
            }

            const data = (await res.json()) as {
                access_token?: string;
                refresh_token?: string;
            };
            const access = String(data?.access_token || "").trim();
            const nextRefresh = String(data?.refresh_token || "").trim();
            if (!access) {
                _handleAuthExpired();
                return null;
            }

            localStorage.setItem("auth_token", access);
            if (nextRefresh) {
                localStorage.setItem("refresh_token", nextRefresh);
            }
            window.dispatchEvent(new Event("ummr-auth-refreshed"));
            return access;
        } catch {
            _handleAuthExpired();
            return null;
        } finally {
            _refreshInFlight = null;
        }
    })();

    return _refreshInFlight;
}

export async function getValidAuthToken(): Promise<string | null> {
    const token = _getStoredToken();
    if (!token) return null;
    if (!_isTokenExpired(token)) return token;
    return refreshAuthToken();
}

const _appendQuery = (url: string, query?: QueryParams): string => {
    if (!query) return url;
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
        if (v === null || v === undefined) continue;
        qs.set(k, String(v));
    }
    const s = qs.toString();
    if (!s) return url;
    return url.includes("?") ? `${url}&${s}` : `${url}?${s}`;
};

const _sleep = (ms: number): Promise<void> =>
    new Promise((resolve) => {
        setTimeout(resolve, ms);
    });

const _isRetriableStatus = (status: number): boolean =>
    status === 429 || status >= 500;

const _createRequestSignal = (
    baseSignal: AbortSignal | null | undefined,
    timeoutMs: number | undefined,
): { signal: AbortSignal | null; cleanup: () => void } => {
    const hasTimeout = Number.isFinite(timeoutMs) && Number(timeoutMs) > 0;
    const hasExternalSignal = Boolean(baseSignal);
    if (!hasTimeout && !hasExternalSignal) {
        return { signal: baseSignal || null, cleanup: () => {} };
    }

    const controller = new AbortController();
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    const onAbort = () => controller.abort();

    if (baseSignal) {
        if (baseSignal.aborted) {
            controller.abort();
        } else {
            baseSignal.addEventListener("abort", onAbort, { once: true });
        }
    }

    if (hasTimeout) {
        timeoutHandle = setTimeout(() => {
            controller.abort();
        }, Number(timeoutMs));
    }

    return {
        signal: controller.signal,
        cleanup: () => {
            if (timeoutHandle !== null) {
                clearTimeout(timeoutHandle);
            }
            if (baseSignal) {
                baseSignal.removeEventListener("abort", onAbort);
            }
        },
    };
};

export async function apiFetch(
    endpoint: string,
    opts?: ApiFetchOptions,
): Promise<Response> {
    const url = _appendQuery(apiUrl(endpoint), opts?.query);
    const headers = new Headers(opts?.headers);
    headers.set("Accept", headers.get("Accept") || "application/json");

    const token = await getValidAuthToken();
    if (token && !headers.get("Authorization")) {
        headers.set("Authorization", `Bearer ${token}`);
    }

    const {
        query: _query,
        skipAuthRefresh = false,
        retry = 0,
        retryDelayMs = 350,
        timeoutMs,
        ...requestInit
    } = opts || {};

    const maxRetries = Math.max(0, Math.trunc(retry));
    let attempt = 0;
    let shouldRetry = true;

    while (shouldRetry) {
        const { signal, cleanup } = _createRequestSignal(
            requestInit.signal || null,
            timeoutMs,
        );
        try {
            let response = await fetch(url, {
                ...requestInit,
                headers,
                signal: signal || undefined,
            });

            if (response.status === 401 && !skipAuthRefresh) {
                const refreshedToken = await refreshAuthToken();
                if (refreshedToken) {
                    const retryHeaders = new Headers(requestInit.headers);
                    retryHeaders.set(
                        "Accept",
                        retryHeaders.get("Accept") || "application/json",
                    );
                    if (!retryHeaders.get("Authorization")) {
                        retryHeaders.set("Authorization", `Bearer ${refreshedToken}`);
                    }
                    response = await fetch(url, {
                        ...requestInit,
                        headers: retryHeaders,
                        signal: signal || undefined,
                    });
                }
            }

            if (_isRetriableStatus(response.status) && attempt < maxRetries) {
                attempt += 1;
                await _sleep(retryDelayMs * attempt);
                continue;
            }

            shouldRetry = false;
            return response;
        } catch (err) {
            if (
                err instanceof DOMException &&
                err.name === "AbortError"
            ) {
                throw err;
            }
            if (attempt >= maxRetries) {
                throw err;
            }
            attempt += 1;
            await _sleep(retryDelayMs * attempt);
        } finally {
            cleanup();
        }
    }

    throw new Error("Request retry loop exited unexpectedly");
}

export async function apiFetchJson<T>(
    endpoint: string,
    opts?: Omit<RequestInit, "body"> & {
        query?: QueryParams;
        json?: unknown;
        skipAuthRefresh?: boolean;
        retry?: number;
        retryDelayMs?: number;
        timeoutMs?: number;
    },
): Promise<T> {
    const headers = new Headers(opts?.headers);
    const init: RequestInit & { query?: QueryParams } = {
        ...opts,
        headers,
    };

    if (Object.prototype.hasOwnProperty.call(opts || {}, "json")) {
        headers.set("Content-Type", "application/json");
        (init as any).body = JSON.stringify((opts as any).json ?? null);
    }

    const res = await apiFetch(endpoint, init);
    if (res.ok) {
        // Some endpoints may legitimately return empty bodies.
        const text = await res.text();
        return (text ? JSON.parse(text) : (null as any)) as T;
    }

    let detail = res.statusText || "Request failed";
    try {
        const text = await res.text();
        if (text) {
            try {
                const parsed = JSON.parse(text);
                detail =
                    String(parsed?.detail || parsed?.error || parsed?.message || text);
            } catch {
                detail = text;
            }
        }
    } catch {
        // ignore
    }
    throw new ApiError(res.status, detail);
}

// Canonical endpoints (REAL API only)
export const API_ENDPOINTS = {
    HEALTH: "/health",
    OPENAPI: "/openapi.json",

    AUTH_ME: "/api/v1/auth/me",
    AUTH_DISCORD_LOGIN: "/api/v1/auth/discord/login",
    AUTH_REFRESH: "/api/v1/auth/refresh",

    SERVERS: "/api/v1/servers/",
    SERVERS_OVERVIEW: "/api/v1/servers/status/overview",
    SERVERS_PERFORMANCE: "/api/v1/servers/metrics/performance",
    SERVERS_ADMIN_ACTION: "/api/v1/servers/admin/action",
    SERVERS_CACHE_REFRESH: "/api/v1/servers/admin/cache/refresh",
    SERVERS_DOCKER_DISCOVERY: "/api/v1/servers/discovery/docker",

    ZOMBOID_STATUS: "/api/v1/servers/zomboid/status",
    ZOMBOID_SCHEDULE: "/api/v1/servers/zomboid/schedule",
    ZOMBOID_PLAYERS: "/api/v1/servers/zomboid/players",
    ZOMBOID_LOGS: "/api/v1/servers/zomboid/logs",
    ZOMBOID_CONFIG: "/api/v1/servers/zomboid/config",
    ZOMBOID_START: "/api/v1/servers/zomboid/start",
    ZOMBOID_RESTART: "/api/v1/servers/zomboid/restart",
    ZOMBOID_BACKUP: "/api/v1/servers/zomboid/backup",
    ZOMBOID_MODSYNC: "/api/v1/servers/zomboid/mods/sync",
    ZOMBOID_WIPE: "/api/v1/servers/zomboid/wipe",
    ZOMBOID_SAVE: "/api/v1/servers/zomboid/save",
    ZOMBOID_BROADCAST: "/api/v1/servers/zomboid/broadcast",
    ZOMBOID_FILES: "/api/v1/servers/zomboid/files",
    ZOMBOID_FILE: (fileId: string) => `/api/v1/servers/zomboid/files/${encodeURIComponent(fileId)}`,
    ZOMBOID_FILE_CUSTOM: "/api/v1/servers/zomboid/files/custom",
    ZOMBOID_FILE_RESTORE: (fileId: string) =>
        `/api/v1/servers/zomboid/files/${encodeURIComponent(fileId)}/restore-template`,
    ZOMBOID_KICK: (username: string) =>
        `/api/v1/servers/zomboid/kick/${encodeURIComponent(username)}`,
    ZOMBOID_BAN: (username: string) =>
        `/api/v1/servers/zomboid/ban/${encodeURIComponent(username)}`,
    ZOMBOID_UNBAN: (username: string) =>
        `/api/v1/servers/zomboid/unban/${encodeURIComponent(username)}`,
    ZOMBOID_SET_TIME: (hour: number) => `/api/v1/servers/zomboid/settime/${hour}`,
    ZOMBOID_OPTIONS: "/api/v1/servers/zomboid/options",
    ZOMBOID_RCON: "/api/v1/servers/zomboid/rcon",

    BOT_STATUS: "/api/v1/bot/status",
    BOT_METRICS: "/api/v1/bot/metrics",
    BOT_STATS_COMMANDS: "/api/v1/bot/stats/commands",
    BOT_STATS_GUILDS: "/api/v1/bot/stats/guilds",
    BOT_STATS_LEVELING: "/api/v1/bot/stats/leveling",
    BOT_STATS_GATEKEEPER: "/api/v1/bot/stats/gatekeeper",
    BOT_LOGS: "/api/v1/bot/logs",
    BOT_ACTION: (action: string) => `/api/v1/bot/action/${action}`,
    BOT_HEALTH: "/api/v1/bot/health",
    BOT_COGS: "/api/v1/bot/cogs",
    BOT_OPS_READINESS: "/api/v1/bot/ops/readiness",
    
    // Discord Control
    DISCORD_GUILDS: "/api/v1/bot/discord/guilds",
    DISCORD_CHANNELS: "/api/v1/bot/discord/channels",
    DISCORD_ROLES: "/api/v1/bot/discord/roles",
    DISCORD_MEMBERS: "/api/v1/bot/discord/members",
    DISCORD_MESSAGES: "/api/v1/bot/discord/messages",
    DISCORD_SEND: "/api/v1/bot/discord/send",
    DISCORD_MEMBER_ACTION: (memberId: number) => `/api/v1/bot/discord/member/${memberId}/action`,
    DISCORD_MEMBER_ROLE: (memberId: number) => `/api/v1/bot/discord/member/${memberId}/role`,

    PUBLIC_DISCORD_ONLINE: "/api/v1/public/discord/online",

    ADMIN_USERS: "/api/v1/admin/users/",
    ADMIN_USER_STATS: "/api/v1/admin/users/stats",
    ADMIN_USER_DETAILS: (id: number) => `/api/v1/admin/users/${id}`,
    ADMIN_USER_ROLE: (id: number) => `/api/v1/admin/users/${id}/role`,
    ADMIN_USER_STATUS: (id: number) => `/api/v1/admin/users/${id}/status`,
    ADMIN_USER_SESSIONS: (id: number) => `/api/v1/admin/users/${id}/sessions`,
    ADMIN_AUDIT_LOGS: "/api/v1/admin/audit",

    ADMIN_ALERTS: "/api/v1/admin/alerts/",
    ADMIN_ALERTS_SUMMARY: "/api/v1/admin/alerts/summary",
    ADMIN_ALERT_RESOLVE: (id: string) => `/api/v1/admin/alerts/${id}/resolve`,

    API_KEYS_CREATE: "/api/v1/api-keys/create",
    API_KEYS_LIST: "/api/v1/api-keys/list",
    API_KEYS_DELETE: (id: number) => `/api/v1/api-keys/${id}`,
    API_KEYS_SCOPES: "/api/v1/api-keys/scopes",

    DATABASE_HEALTH: "/api/v1/database/health",
    DATABASE_METRICS: "/api/v1/database/metrics",
    DATABASE_BACKUPS: "/api/v1/database/backups",

    SYSTEM_INFO: "/api/v1/system/info",
    SYSTEM_PROCESSES: "/api/v1/system/processes",

    TERMINAL_SESSIONS: "/api/v1/terminal/sessions",
    TERMINAL_EXECUTE: "/api/v1/terminal/execute",
    TERMINAL_SESSION_DELETE: (id: string) => `/api/v1/terminal/sessions/${id}`,

    RCON_TEST: (serverId: string) =>
        `/api/v1/rcon/${encodeURIComponent(serverId)}/test`,
    RCON_PLAYERS: (serverId: string) =>
        `/api/v1/rcon/${encodeURIComponent(serverId)}/players`,
    RCON_COMMAND: (serverId: string) =>
        `/api/v1/rcon/${encodeURIComponent(serverId)}/command`,
    RCON_KICK: (serverId: string) =>
        `/api/v1/rcon/${encodeURIComponent(serverId)}/kick`,
    RCON_BAN: (serverId: string) =>
        `/api/v1/rcon/${encodeURIComponent(serverId)}/ban`,
    RCON_BROADCAST: (serverId: string) =>
        `/api/v1/rcon/${encodeURIComponent(serverId)}/broadcast`,
    RCON_SAVE: (serverId: string) =>
        `/api/v1/rcon/${encodeURIComponent(serverId)}/save`,
    RCON_TIME: (serverId: string, value: string) =>
        `/api/v1/rcon/${encodeURIComponent(serverId)}/time/${encodeURIComponent(value)}`,
    RCON_WEATHER: (serverId: string, weather: string) =>
        `/api/v1/rcon/${encodeURIComponent(serverId)}/weather/${encodeURIComponent(weather)}`,

    ADMIN_SETTINGS_ALL: "/api/v1/admin/settings/",
    ADMIN_SETTINGS_SYSTEM: "/api/v1/admin/settings/system",
    ADMIN_SETTINGS_SECURITY: "/api/v1/admin/settings/security",
    ADMIN_SETTINGS_GAMING: "/api/v1/admin/settings/gaming",
    ADMIN_SETTINGS_BACKUP: "/api/v1/admin/settings/backup",
    ADMIN_SETTINGS_MAINTENANCE: "/api/v1/admin/settings/maintenance",
} as const;

// Helper per debug
export const getApiInfo = () => {
    if (typeof window !== "undefined") {
        return {
            hostname: window.location.hostname,
            baseUrl: getApiBaseUrl(),
            environment: window.location.hostname.includes("unmondomarrano")
                ? "production"
                : "development",
        };
    }
    return {
        hostname: "unknown",
        baseUrl: getApiBaseUrl(),
        environment: "server",
    };
};
